import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, Loader2, Check } from 'lucide-react';
import { getApplicationForSetup, createProductFromApplication } from '@/data/vendorSetup';
import { VendorApplicationRow } from '@/contracts/vendor-application';
import { VendorSetupData, VendorSetupMedia, VendorSetupDetails, VendorSetupPricing, VendorSetupFeatures, VendorSetupSocialProof } from '@/contracts/vendor-setup';
import { SetupMediaStep } from '@/components/vendor/SetupMediaStep';
import { SetupDetailsStep } from '@/components/vendor/SetupDetailsStep';
import { SetupPricingStep } from '@/components/vendor/SetupPricingStep';
import { SetupFeaturesStep } from '@/components/vendor/SetupFeaturesStep';
import { SetupSocialProofStep } from '@/components/vendor/SetupSocialProofStep';
import { SetupPreviewStep } from '@/components/vendor/SetupPreviewStep';

const STEPS = [
  { id: 'media', title: 'Media & Branding', description: 'Upload your logo and product images' },
  { id: 'details', title: 'Product Details', description: 'Describe your product or service' },
  { id: 'pricing', title: 'Pricing Plans', description: 'Set up your pricing structure' },
  { id: 'features', title: 'Features & Benefits', description: 'Highlight what makes you unique' },
  { id: 'social-proof', title: 'Social Proof', description: 'Add testimonials and success metrics' },
  { id: 'preview', title: 'Preview & Submit', description: 'Review and submit for approval' },
];

export default function VendorSetup() {
  const { applicationId } = useParams<{ applicationId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [application, setApplication] = useState<VendorApplicationRow | null>(null);
  
  // Form data for each step
  const [mediaData, setMediaData] = useState<VendorSetupMedia>({
    logo_url: undefined,
    hero_image_url: undefined,
    gallery: [],
    demo_video_url: undefined,
  });
  
  const [detailsData, setDetailsData] = useState<VendorSetupDetails>({
    title: '',
    subtitle: '',
    description: '',
    categories: [],
    best_for: [],
    service_type: 'service',
  });
  
  const [pricingData, setPricingData] = useState<VendorSetupPricing>({
    price: 0,
    msrp: undefined,
    price_duration: 'mo',
    plans: [],
  });
  
  const [featuresData, setFeaturesData] = useState<VendorSetupFeatures>({
    features: [],
    feature_highlights: [],
    at_a_glance: undefined,
  });
  
  const [socialProofData, setSocialProofData] = useState<VendorSetupSocialProof>({
    testimonial_videos: [],
    roi_metrics: undefined,
  });

  useEffect(() => {
    if (!user) {
      navigate('/auth?returnTo=/sell');
      return;
    }
    
    if (!applicationId) {
      navigate('/sell');
      return;
    }
    
    loadApplication();
  }, [user, applicationId]);

  async function loadApplication() {
    setLoading(true);
    try {
      const app = await getApplicationForSetup(applicationId!);
      if (!app) {
        toast.error('Application not found');
        navigate('/sell');
        return;
      }
      
      setApplication(app);
      
      // Pre-populate from application
      setMediaData({
        logo_url: app.logo_url || undefined,
        hero_image_url: undefined,
        gallery: (app.screenshots as string[]) || [],
        demo_video_url: app.demo_video_url || undefined,
      });
      
      setDetailsData({
        title: app.product_name,
        subtitle: app.product_brief_description,
        description: app.product_detailed_description,
        categories: [app.product_category],
        best_for: [app.target_audience],
        service_type: app.service_type as 'service' | 'education' | 'gear',
      });
      
      setPricingData({
        price: Number(app.proposed_deal_price) || 0,
        msrp: Number(app.current_price) || undefined,
        price_duration: 'mo',
        plans: [],
      });
      
      setFeaturesData({
        features: (app.key_features as string[]) || [],
        feature_highlights: [],
        at_a_glance: {
          summary: app.problem_solved,
          who_its_for: app.target_audience,
          what_you_get: app.deal_includes,
        },
      });
      
    } catch (error) {
      console.error('Error loading application:', error);
      toast.error('Failed to load application');
    } finally {
      setLoading(false);
    }
  }

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const setupData: VendorSetupData = {
        media: mediaData,
        details: detailsData,
        pricing: pricingData,
        features: featuresData,
        socialProof: socialProofData,
      };
      
      await createProductFromApplication(applicationId!, setupData);
      
      toast.success('Your listing has been submitted for review!');
      navigate('/vendor/dashboard');
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Failed to submit listing');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold">Set Up Your Listing</h1>
          <p className="text-muted-foreground mt-1">
            Complete these steps to create your product page
          </p>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-background border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].title}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}% complete</span>
          </div>
          <Progress value={progress} className="h-2" />
          
          {/* Step indicators */}
          <div className="flex justify-between mt-4">
            {STEPS.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(index)}
                className={`flex flex-col items-center gap-1 ${
                  index <= currentStep ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index < currentStep 
                    ? 'bg-primary text-primary-foreground' 
                    : index === currentStep 
                      ? 'bg-primary/20 text-primary border-2 border-primary' 
                      : 'bg-muted text-muted-foreground'
                }`}>
                  {index < currentStep ? <Check className="h-4 w-4" /> : index + 1}
                </div>
                <span className="text-xs hidden sm:block">{step.title}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Card>
          <CardContent className="p-6">
            {currentStep === 0 && (
              <SetupMediaStep data={mediaData} onChange={setMediaData} />
            )}
            {currentStep === 1 && (
              <SetupDetailsStep data={detailsData} onChange={setDetailsData} />
            )}
            {currentStep === 2 && (
              <SetupPricingStep data={pricingData} onChange={setPricingData} />
            )}
            {currentStep === 3 && (
              <SetupFeaturesStep data={featuresData} onChange={setFeaturesData} />
            )}
            {currentStep === 4 && (
              <SetupSocialProofStep data={socialProofData} onChange={setSocialProofData} />
            )}
            {currentStep === 5 && (
              <SetupPreviewStep
                media={mediaData}
                details={detailsData}
                pricing={pricingData}
                features={featuresData}
                socialProof={socialProofData}
              />
            )}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext}>
              Next
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit for Review'
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
